import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { useSession } from './SessionContext';
import { defaultAppSettings, loadAppSettings } from './appSettings';
import BookingCalendarPanel from './booking/BookingCalendarPanel';
import BookingPageHeader from './booking/BookingPageHeader';
import BookingModal from './booking/BookingModal';
import BookingDetailsModal from './booking/BookingDetailsModal';
import './big-calendar-dark.css';
import {
  fetchAircraftList,
  fetchBookingRows,
  fetchTrainingBookingOptions,
  fetchUsersList,
} from './booking/bookingService';
import {
  bookingRangesOverlap,
  isAnotherDayReturnDateValid,
  submitBooking,
} from './booking/bookingSubmission';
import {
  addMinutes,
  addOneHour,
  formatBookingRange,
  formatLocalDate,
  formatLocalTime,
  getBookingEndDate,
  getBookingStartDate,
  getExplicitBookingEndDate,
  normalizeAirfieldCode,
  normalizeClockValue,
  parseDateOnly,
  parseDateTime,
} from './booking/bookingUtils';
import { performBookingDeletion } from './booking/bookingDeletionGuard';
import {
  fetchOrganisationBaseAirfieldIcao,
  resolveBaseAirfield,
} from './booking/baseAirfieldResolution';

const pageStyle = {
  width: '100%',
  maxWidth: 1100,
  margin: '2rem auto',
  background: '#181a2a',
  borderRadius: 16,
  padding: '2rem',
  color: 'white',
  boxShadow: '0 4px 32px rgba(0,0,0,0.18)',
  boxSizing: 'border-box',
};

const buttonStyle = {
  background: '#1ecfff',
  color: '#181a2a',
  border: 'none',
  borderRadius: 8,
  padding: '0.65rem 1rem',
  fontSize: '1rem',
  fontWeight: 700,
  cursor: 'pointer',
};

const secondaryButtonStyle = {
  ...buttonStyle,
  background: '#b6eaff',
};

const inputStyle = {
  width: '100%',
  padding: '0.65rem 0.8rem',
  borderRadius: 8,
  border: '1px solid #4d79a8',
  background: '#10233a',
  color: 'white',
  fontSize: '1rem',
  boxSizing: 'border-box',
};

function buildAircraftDisplayLabel(aircraft) {
  if (!aircraft) return 'Aircraft';
  return `${aircraft.registration}${aircraft.icao_type ? ` (${aircraft.icao_type})` : ''}`;
}

function getAircraftChipStyle(colour, size = 12) {
  return {
    display: 'inline-block',
    width: size,
    height: size,
    minWidth: size,
    borderRadius: 999,
    background: colour,
    boxShadow: '0 0 0 2px rgba(255,255,255,0.12)',
  };
}

const aircraftColours = [
  '#2563eb',
  '#facc15',
  '#ec4899',
  '#16a34a',
  '#f97316',
  '#8b5cf6',
  '#06b6d4',
  '#ef4444',
  '#84cc16',
  '#14b8a6',
  '#eab308',
  '#3b82f6',
];

const lightAircraftColours = new Set(['#facc15', '#84cc16', '#eab308']);

function getFallbackAircraftColour(identifier) {
  const text = String(identifier || '').trim().toUpperCase();
  if (!text) return aircraftColours[0];

  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash * 31 + text.charCodeAt(index)) >>> 0;
  }

  return aircraftColours[hash % aircraftColours.length];
}

function getAircraftServiceabilityMeta(status) {
  const normalized = String(status || 'serviceable').trim().toLowerCase();

  if (normalized === 'aog') {
    return { label: 'AOG', icon: '⛔', text: '#ffb3b3', background: 'rgba(255, 77, 77, 0.16)', border: '#ff4d4d' };
  }

  if (normalized === 'defect') {
    return { label: 'DEFECT', icon: '🔴', text: '#ffb3b3', background: 'rgba(255, 110, 110, 0.14)', border: '#ff6e6e' };
  }

  if (normalized === 'deferred') {
    return { label: 'DEFERRED', icon: '🟠', text: '#ffd27a', background: 'rgba(255, 204, 102, 0.14)', border: '#ffcc66' };
  }

  return { label: 'SERVICEABLE', icon: '🟢', text: '#8ff4b6', background: 'rgba(125, 240, 168, 0.12)', border: '#7df0a8' };
}

function getAircraftColour(aircraft, aircraftList) {
  if (!aircraft) {
    return getFallbackAircraftColour('');
  }

  const numericId = Number(aircraft.id);
  if (Number.isFinite(numericId) && numericId > 0) {
    return aircraftColours[(numericId - 1) % aircraftColours.length];
  }

  const orderedAircraft = [...(aircraftList || [])].sort((left, right) => Number(left?.id || 0) - Number(right?.id || 0));
  const aircraftIndex = orderedAircraft.findIndex((entry) => Number(entry?.id) === Number(aircraft.id));

  if (aircraftIndex >= 0) {
    return aircraftColours[aircraftIndex % aircraftColours.length];
  }

  return getFallbackAircraftColour(aircraft.registration || aircraft.id);
}

function createAircraftEventStyles(aircraft, aircraftList) {
  const colour = getAircraftColour(aircraft, aircraftList);
  const textColor = lightAircraftColours.has(colour.toLowerCase()) ? '#10233a' : '#ffffff';

  return {
    backgroundColor: colour,
    borderColor: colour,
    textColor,
  };
}

function readBriefingAirfield(briefingData, keys, fallback = '') {
  for (const key of keys) {
    const value = String(briefingData?.[key] || '').trim().toUpperCase();
    if (value) return value;
  }

  return String(fallback || '').trim().toUpperCase();
}

export function getNextCalendarDate(dateValue) {
  const date = parseDateOnly(dateValue) || new Date();
  date.setDate(date.getDate() + 1);
  return formatLocalDate(date);
}

export { bookingRangesOverlap, isAnotherDayReturnDateValid };

export function getBookingDeletionErrorMessage(reason) {
  if (reason === 'booking_started') {
    return "This booking has already started and can no longer be deleted. If the flight did not take place, use 'Did Not Take Place' instead.";
  }

  return '';
}

export default function BookAircraft() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedBookingId = String(
    searchParams.get('bookingId') || ''
  ).trim();
  const requestedOrigin = String(
    searchParams.get('origin') || ''
  ).trim().toLowerCase();
  const requestedDate = String(searchParams.get('date') || '').trim();
  const selectedDateFromUrl = parseDateOnly(requestedDate)
    ? requestedDate
    : '';
  const session = useSession();
  const user = session?.user;
  const organisationId =
    session?.profile?.organisation_id ||
    user?.organisation_id ||
    user?.user_metadata?.organisation_id ||
    user?.app_metadata?.organisation_id ||
    '';
  const calendarRef = useRef(null);
  const tenantScope = `${user?.id || ''}:${session?.profile?.organisation_id || ''}`;
  const tenantScopeRef = useRef(tenantScope);
  tenantScopeRef.current = tenantScope;
  const bookingsPollTimerRef = useRef(null);
  const fetchBookingsRef = useRef(null);
  const openedRequestedBookingRef = useRef('');
  const detailsOriginRef = useRef('calendar');
  const appSettings = useMemo(
    () =>
      organisationId
        ? loadAppSettings(organisationId)
        : defaultAppSettings,
    [organisationId]
  );
  const settingsBaseAirfield = String(appSettings?.operational?.baseAirfield || '').trim().toUpperCase();
  const [databaseBaseAirfield, setDatabaseBaseAirfield] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadClubBaseAirfield() {
      const icao = await fetchOrganisationBaseAirfieldIcao(
        supabase,
        organisationId
      );

      if (!cancelled) {
        setDatabaseBaseAirfield(icao);
      }
    }

    loadClubBaseAirfield();

    return () => {
      cancelled = true;
    };
  }, [organisationId]);

  const baseAirfield = resolveBaseAirfield(
    databaseBaseAirfield,
    settingsBaseAirfield
  );
  const [aircraftList, setAircraftList] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [events, setEvents] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [trainingOptions, setTrainingOptions] = useState({
    studentProfiles: [],
    instructorProfiles: [],
    enrolments: [],
  });
  const trainingOptionsLoadRef = useRef({ tenantScope, status: 'idle' });
  const [trainingOptionsLoading, setTrainingOptionsLoading] = useState(false);
  const [trainingOptionsError, setTrainingOptionsError] = useState('');
  const [selectedDate, setSelectedDate] = useState(
    selectedDateFromUrl || formatLocalDate(new Date())
  );
  const [calendarDate, setCalendarDate] = useState(
    selectedDateFromUrl ? parseDateOnly(selectedDateFromUrl) : new Date()
  );
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [activeBooking, setActiveBooking] = useState(null);
  const [editingBookingId, setEditingBookingId] = useState('');
  const [deletingBooking, setDeletingBooking] = useState(false);
  const [successBookingId, setSuccessBookingId] = useState('');
  const [error, setError] = useState('');
  const [detailsError, setDetailsError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const startDatePickerRef = useRef(null);
  const endDatePickerRef = useRef(null);

  const [form, setForm] = useState({
    bookingType: 'normal',
    studentUserId: '',
    instructorUserId: '',
    courseEnrolmentId: '',
    aircraft: '',
    flightType: 'local',
    date: formatLocalDate(new Date()),
    time: '',
    durationInput: '',
    showEndDate: false,
    endDate: formatLocalDate(new Date()),
    endTime: '',
    departureAirfield: baseAirfield,
    destinationAirfield: '',
    notes: '',
  });

  const currentRole = String(session?.profile?.role || user?.user_metadata?.role || user?.app_metadata?.role || '').trim().toLowerCase();
  const isActiveMember = session?.profile?.is_active !== false;
  const wingitUserId = String(session?.profile?.id || '').trim();
  const canCreateTrainingBooking = Boolean(
    user?.id && isActiveMember && (
      ['owner', 'admin'].includes(currentRole)
      || trainingOptions.instructorProfiles.some((profile) => String(profile.user_id) === wingitUserId && profile.status === 'active')
    )
  );
  const isActiveBookingOwner = !!activeBooking && String(activeBooking.user_id) === String(user?.id || '');
  const selectedAircraft = aircraftList.find((aircraft) => String(aircraft.id) === String(form.aircraft));

  useEffect(() => {
    setBookings([]);
    setEvents([]);
    setAircraftList([]);
    setUsersMap({});
    setTrainingOptions({ studentProfiles: [], instructorProfiles: [], enrolments: [] });
    trainingOptionsLoadRef.current = { tenantScope, status: 'idle' };
    setTrainingOptionsError('');
    setActiveBooking(null);
    setDetailsModalOpen(false);
    fetchAircraft();
    fetchUsers();
    fetchTrainingOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantScope]);

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aircraftList, usersMap]);

  useEffect(() => {
    buildEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings, aircraftList, usersMap, trainingOptions]);

  useEffect(() => {
    if (!selectedDateFromUrl) return;
    setSelectedDate(selectedDateFromUrl);
    setCalendarDate(parseDateOnly(selectedDateFromUrl));
  }, [selectedDateFromUrl]);

  function openDayView(dateValue) {
    const nextDate = formatLocalDate(parseDateOnly(dateValue));
    if (!nextDate) return;

    setSelectedDate(nextDate);
    setCalendarDate(parseDateOnly(nextDate));
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('date', nextDate);
    setSearchParams(nextSearchParams, {
      state: {
        ...location.state,
        bookingDayEntry: true,
      },
    });
  }

  function returnToCalendar() {
    if (location.state?.bookingDayEntry) {
      navigate(-1);
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('date');
    setSearchParams(nextSearchParams, { replace: true });
  }

  function changeDayView(dateValue) {
    const nextDate = formatLocalDate(parseDateOnly(dateValue));
    if (!nextDate) return;
    setSelectedDate(nextDate);
    setCalendarDate(parseDateOnly(nextDate));
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('date', nextDate);
    setSearchParams(nextSearchParams, {
      replace: true,
      state: location.state,
    });
  }

  useEffect(() => {
    if (!startDatePickerOpen && !endDatePickerOpen) return undefined;

    function handleOutsideClick(event) {
      if (startDatePickerRef.current && !startDatePickerRef.current.contains(event.target)) {
        setStartDatePickerOpen(false);
      }
      if (endDatePickerRef.current && !endDatePickerRef.current.contains(event.target)) {
        setEndDatePickerOpen(false);
      }
    }

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [startDatePickerOpen, endDatePickerOpen]);

  async function fetchAircraft() {
    setError('');

    const { data, error } = await fetchAircraftList(supabase);
    if (tenantScopeRef.current !== tenantScope) return;

    if (error) {
      setError('Could not load aircraft list.');
      setAircraftList([]);
    } else {
      setAircraftList(data || []);
    }
  }

  async function fetchUsers() {
    const { data, error } = await fetchUsersList(supabase);
    if (tenantScopeRef.current !== tenantScope) return;

    if (error) {
      setUsersMap({});
      return;
    }
    if (!error && data) {
      const map = {};
      data.forEach((u) => {
        map[u.id] = u;
      });
      setUsersMap(map);
    }
  }

  async function fetchTrainingOptions({ refresh = false } = {}) {
    const previousLoad = trainingOptionsLoadRef.current;
    if (
      previousLoad.tenantScope !== tenantScope ||
      previousLoad.status === 'loading' ||
      (!refresh && previousLoad.status === 'loaded')
    ) return;
    const load = { tenantScope, status: 'loading' };
    trainingOptionsLoadRef.current = load;
    setTrainingOptionsLoading(true);
    if (refresh) {
      setTrainingOptions({ studentProfiles: [], instructorProfiles: [], enrolments: [] });
    }

    try {
      const result = await fetchTrainingBookingOptions(supabase);
      if (tenantScopeRef.current !== tenantScope || trainingOptionsLoadRef.current !== load) return;
      if (result.error) throw result.error;
      setTrainingOptions({
        studentProfiles: result.studentProfiles || [],
        instructorProfiles: result.instructorProfiles || [],
        enrolments: result.enrolments || [],
      });
      load.status = 'loaded';
      setTrainingOptionsError('');
    } catch (loadError) {
      if (tenantScopeRef.current !== tenantScope || trainingOptionsLoadRef.current !== load) return;
      load.status = 'failed';
      setTrainingOptions({ studentProfiles: [], instructorProfiles: [], enrolments: [] });
      setTrainingOptionsError(`Could not load training selectors. ${loadError?.message || 'Please try again.'}`);
    } finally {
      if (tenantScopeRef.current === tenantScope && trainingOptionsLoadRef.current === load) {
        setTrainingOptionsLoading(false);
      }
    }
  }

  async function fetchBookings(options = {}) {
    const { data, error } = await fetchBookingRows(supabase);
    if (tenantScopeRef.current !== tenantScope) return;

    if (error) {
      setError('Could not load existing bookings.');
      setBookings([]);
      return;
    }

    const nextBookings = data || [];

    setBookings(nextBookings);

    if (options.source === 'poll') {
      console.log(
        'Booking poll refreshed',
        new Date().toISOString(),
        nextBookings.length
      );
    }
  }

  useEffect(() => {
    fetchBookingsRef.current = fetchBookings;
  });

  useEffect(() => {
    function stopBookingsPolling() {
      if (bookingsPollTimerRef.current) {
        window.clearInterval(bookingsPollTimerRef.current);
        bookingsPollTimerRef.current = null;
      }
    }

    function startBookingsPolling() {
      stopBookingsPolling();

      if (document.visibilityState !== 'visible') {
        return;
      }

      bookingsPollTimerRef.current = window.setInterval(() => {
        fetchBookingsRef.current?.({ source: 'poll' });
      }, 10000);
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        fetchBookingsRef.current?.({ source: 'poll' });
        startBookingsPolling();
        return;
      }

      stopBookingsPolling();
    }

    if (document.visibilityState === 'visible') {
      startBookingsPolling();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopBookingsPolling();
    };
  }, []);

  function buildEvents() {
    const mapped = bookings.map((b) => {
      const aircraft = aircraftList.find((a) => Number(a.id) === Number(b.aircraft_id));
      const startDate = getBookingStartDate(b);
      const endDate = getBookingEndDate(b);
      const explicitEnd = getExplicitBookingEndDate(b);
      const pilot = usersMap[b.user_id];
      const student = usersMap[b.student_user_id];
      const instructor = usersMap[b.instructor_user_id];
      const course = trainingOptions.enrolments.find(
        (enrolment) => String(enrolment.id) === String(b.course_enrolment_id)
      )?.training_courses;
      const aircraftStyles = createAircraftEventStyles(aircraft || { id: b.aircraft_id, registration: b.aircraft_id }, aircraftList);

      if (!startDate || !endDate) return null;

      const durationSpanMinutes = Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 60000));
      const crossesDateBoundary = formatLocalDate(startDate) !== formatLocalDate(endDate);
      const hasExplicitCrossDayRange = !!explicitEnd && explicitEnd > startDate && formatLocalDate(startDate) !== formatLocalDate(explicitEnd);
      const spansMultipleDays = crossesDateBoundary && (hasExplicitCrossDayRange || durationSpanMinutes >= 12 * 60);

      const eventStartDate = formatLocalDate(startDate);
      const endExclusive = new Date(spansMultipleDays ? endDate : startDate);
      endExclusive.setDate(endExclusive.getDate() + 1);
      const eventTitle = `${formatLocalTime(startDate)}–${formatLocalTime(endDate)} ${aircraft?.registration || 'Booked'}`;

      return {
        id: String(b.id),
        title: eventTitle,
        start: eventStartDate,
        end: formatLocalDate(endExclusive),
        allDay: true,
        ...aircraftStyles,
        classNames: ['wingit-booking', ...(b.booking_type === 'training' ? ['wingit-booking--training'] : [])],
        extendedProps: {
          ...b,
          bookingType: b.booking_type || 'normal',
          aircraftRegistration: aircraft?.registration || '',
          aircraftType: aircraft?.icao_type || '',
          pilotName: pilot?.full_name || pilot?.email || 'Booked',
          studentName: student?.full_name || student?.email || 'Student',
          instructorName: instructor?.full_name || instructor?.email || 'Instructor',
          courseLabel: course ? [course.code, course.name].filter(Boolean).join(' · ') : '',
          bookingRange: formatBookingRange(b),
          aircraftColour: aircraftStyles.backgroundColor,
          spanMinutes: durationSpanMinutes,
        },
      };
    }).filter(Boolean);

    setEvents(mapped);
  }

  const openBookingDetails = useCallback((booking, options = {}) => {
    const origin = String(options?.origin || 'calendar')
      .trim()
      .toLowerCase();
    detailsOriginRef.current =
      origin === 'dashboard' ? 'dashboard' : 'calendar';
    setDetailsError('');

    const aircraft = aircraftList.find(
      (a) => Number(a.id) === Number(booking.aircraft_id)
    );
    const pilot = usersMap[booking.user_id];
    const student = usersMap[booking.student_user_id];
    const instructor = usersMap[booking.instructor_user_id];
    const course = trainingOptions.enrolments.find(
      (enrolment) => String(enrolment.id) === String(booking.course_enrolment_id)
    )?.training_courses;

    setActiveBooking({
      ...booking,
      aircraftRegistration:
        aircraft?.registration || 'Aircraft',
      pilotName:
        pilot?.full_name ||
        pilot?.email ||
        'Booked',
      pilotEmail: pilot?.email || '',
      pilotPhone: pilot?.phone || '',
      bookingType: booking.booking_type || 'normal',
      studentName: student?.full_name || student?.email || '',
      instructorName: instructor?.full_name || instructor?.email || '',
      courseLabel: course ? [course.code, course.name].filter(Boolean).join(' · ') : '',
    });

    setDetailsModalOpen(true);
  }, [aircraftList, usersMap, trainingOptions]);

  useEffect(() => {
    openedRequestedBookingRef.current = '';
  }, [requestedBookingId]);

  useEffect(() => {
    if (!requestedBookingId) return;
    if (openedRequestedBookingRef.current === requestedBookingId) {
      return;
    }

    const requestedBooking = bookings.find(
      (booking) => String(booking.id) === requestedBookingId
    );

    if (!requestedBooking) return;

    openedRequestedBookingRef.current = requestedBookingId;
    if (requestedBooking.date) {
      setSelectedDate(requestedBooking.date);
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set('date', requestedBooking.date);
      setSearchParams(nextSearchParams, { replace: true });
    }
    openBookingDetails(requestedBooking, {
      origin: requestedOrigin,
    });
  }, [
    requestedBookingId,
    requestedOrigin,
    bookings,
    aircraftList,
    usersMap,
    openBookingDetails,
    searchParams,
    setSearchParams,
  ]);

  function dismissDetailsModal() {
    setDetailsModalOpen(false);
    setActiveBooking(null);
    setDetailsError('');
  }

  function closeDetailsModalWithReturn() {
    const returnOrigin = detailsOriginRef.current;

    dismissDetailsModal();

    if (returnOrigin === 'dashboard') {
      navigate('/dashboard');
    }
  }

  const calendarRenderKey = events
    .map((evt) => {
      const startMs = evt.start instanceof Date ? evt.start.getTime() : Date.parse(String(evt.start || ''));
      const endMs = evt.end instanceof Date ? evt.end.getTime() : Date.parse(String(evt.end || ''));
      return `${evt.id}:${Number.isFinite(startMs) ? startMs : 'na'}:${Number.isFinite(endMs) ? endMs : 'na'}:${evt.allDay ? '1' : '0'}:${evt.title || ''}`;
    })
    .join('|');

  function formatDurationInput(minutesValue) {
    const minutes = Number(minutesValue);
    if (!Number.isFinite(minutes) || minutes <= 0) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}:${String(mins).padStart(2, '0')}`;
  }

  function parseDurationInputMinutes(value) {
    const text = String(value || '').trim();
    if (!text) return null;

    if (/^\d+$/.test(text)) {
      const hours = Number(text);
      return Number.isFinite(hours) && hours > 0 ? hours * 60 : null;
    }

    const match = text.match(/^(\d{1,3}):(\d{1,2})$/);
    if (!match) return null;

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    if (hours < 0 || minutes < 0 || minutes >= 60) return null;

    const total = hours * 60 + minutes;
    return total > 0 ? total : null;
  }

  function deriveDurationInput(startDate, endDate) {
    if (!startDate || !endDate || endDate <= startDate) return '';
    const minutes = Math.round((endDate.getTime() - startDate.getTime()) / 60000);
    return formatDurationInput(minutes);
  }

  function applyDurationInput(prevForm, nextDurationInput) {
    const durationInput = String(nextDurationInput || '').trim();
    const base = { ...prevForm, durationInput };
    const parsedMinutes = parseDurationInputMinutes(durationInput);

    if (!durationInput || !Number.isFinite(parsedMinutes) || parsedMinutes <= 0) {
      return base;
    }

    const start = parseDateTime(base.date, base.time);
    if (!start) return base;

    const end = addMinutes(start, parsedMinutes);
    return {
      ...base,
      endDate: formatLocalDate(end),
      endTime: formatLocalTime(end),
    };
  }

  function applyDurationPreset(prevForm, presetValue) {
    const preset = String(presetValue || '').trim();
    if (!preset) return prevForm;

    if (preset === '__future_day__') {
      const startDate = parseDateOnly(prevForm.date) || new Date();
      const nextDay = new Date(startDate.getTime());
      nextDay.setDate(nextDay.getDate() + 1);
      const existingEndDate = parseDateOnly(prevForm.endDate);
      const resolvedEndDate = existingEndDate && existingEndDate > startDate
        ? existingEndDate
        : nextDay;

      return {
        ...prevForm,
        durationInput: '',
        showEndDate: true,
        endDate: formatLocalDate(resolvedEndDate),
        endTime: prevForm.endTime || normalizeClockValue(prevForm.time) || '',
      };
    }

    const sameDayBase = {
      ...prevForm,
      showEndDate: false,
      endDate: prevForm.date,
    };
    return applyDurationInput(sameDayBase, preset);
  }

  function openBookingModal(aircraftId = '', dateOverride = '', bookingToEdit = null, startTimeOverride = '') {
    setError('');
    setSuccessMessage('');
    setStartDatePickerOpen(false);
    setEndDatePickerOpen(false);
    if (!bookingToEdit) {
      setSuccessBookingId('');
    }

    const bookingDate = dateOverride || selectedDate;

    if (bookingToEdit) {
      if (bookingToEdit.booking_type === 'training' && canCreateTrainingBooking) {
        fetchTrainingOptions({ refresh: true });
      }
      const canEditBooking = bookingToEdit.booking_type === 'training'
        ? canCreateTrainingBooking
        : String(bookingToEdit.user_id) === String(user?.id || '');
      if (!canEditBooking) {
        setError('You can only edit your own bookings.');
        return;
      }

      const briefingData = bookingToEdit.briefing_data || {};
      const endDate = getBookingEndDate(bookingToEdit);

      const departureAirfield = readBriefingAirfield(
        briefingData,
        ['departureAirfield', 'departure_airfield', 'fromAirfield', 'from_airfield', 'departure', 'from'],
        baseAirfield
      );

      const destinationAirfield = readBriefingAirfield(
        briefingData,
        ['destinationAirfield', 'destination_airfield', 'toAirfield', 'to_airfield', 'destination', 'to'],
        ''
      );

      const storedFlightType = String(
        briefingData.flightType ||
        briefingData.flight_type ||
        ''
      )
        .trim()
        .toLowerCase();

      const flightType =
        storedFlightType === 'local' ||
        storedFlightType === 'circuits'
          ? storedFlightType
          : 'land-away';
      setEditingBookingId(String(bookingToEdit.id));
      setForm({
        bookingType: bookingToEdit.booking_type === 'training' ? 'training' : 'normal',
        studentUserId: bookingToEdit.student_user_id || '',
        instructorUserId: bookingToEdit.instructor_user_id || '',
        courseEnrolmentId: bookingToEdit.course_enrolment_id || '',
        aircraft: Number(bookingToEdit.aircraft_id) || '',
        flightType,
        date: bookingToEdit.date || bookingDate,
        time: normalizeClockValue(bookingToEdit.time),
        durationInput: (() => {
          const mins = Number(bookingToEdit.duration);
          return Number.isFinite(mins) && mins > 0 ? formatDurationInput(mins) : '';
        })(),
        showEndDate: (() => {
          const bookingEnd = bookingToEdit.end_date || (endDate ? formatLocalDate(endDate) : '');
          return !!bookingEnd && bookingEnd !== (bookingToEdit.date || bookingDate);
        })(),
        endDate: bookingToEdit.end_date || (endDate ? formatLocalDate(endDate) : bookingToEdit.date || bookingDate),
        endTime: normalizeClockValue(bookingToEdit.end_time) || (endDate ? formatLocalTime(endDate) : addOneHour(bookingToEdit.date || bookingDate, bookingToEdit.time)),
        departureAirfield,
        destinationAirfield,
        notes: bookingToEdit.notes || '',
      });
      setBookingModalOpen(true);
      return;
    }

    setEditingBookingId('');
const resolvedStartTime = normalizeClockValue(startTimeOverride);
const resolvedEndTime = resolvedStartTime
? addOneHour(bookingDate, resolvedStartTime)
: '';
  
  

  
  

setForm({
  bookingType: 'normal',
  studentUserId: '',
  instructorUserId: '',
  courseEnrolmentId: '',
  aircraft: aircraftId || '',
  flightType: 'local',
  date: bookingDate,
  time: resolvedStartTime,
  durationInput: resolvedStartTime ? '1:00' : '',
  showEndDate: false,
  endDate: bookingDate,
  endTime: resolvedEndTime,
  departureAirfield: baseAirfield,
  destinationAirfield: baseAirfield,
  notes: '',
});
    setBookingModalOpen(true);
  }

  function handleChange(event) {
    const { name, value } = event.target;

    if (name === 'bookingType') {
      setForm((prev) => ({
        ...prev,
        bookingType: value === 'training' ? 'training' : 'normal',
        studentUserId: '',
        instructorUserId: '',
        courseEnrolmentId: '',
      }));
      if (value === 'training' && canCreateTrainingBooking) {
        fetchTrainingOptions({ refresh: true });
      }
      return;
    }

    if (name === 'studentUserId') {
      setForm((prev) => ({ ...prev, studentUserId: value, courseEnrolmentId: '' }));
      return;
    }

    if (name === 'courseEnrolmentId') {
      const selectedEnrolment = trainingOptions.enrolments.find(
        (enrolment) => String(enrolment.id) === String(value)
      );
      const assignedInstructor = trainingOptions.instructorProfiles.find(
        (profile) => String(profile.id) ===
          String(selectedEnrolment?.primary_instructor_profile_id || '')
      );
      setForm((prev) => ({
        ...prev,
        courseEnrolmentId: value,
        instructorUserId: assignedInstructor?.user_id || prev.instructorUserId,
      }));
      return;
    }

    if (name === 'flightType') {
      const nextFlightType =
        value === 'circuits' ||
        value === 'land-away'
          ? value
          : 'local';

      setForm((prev) => {
        if (nextFlightType === 'land-away') {
          return {
            ...prev,
            flightType: 'land-away',
            departureAirfield: '',
            destinationAirfield: '',
            endDate: getNextCalendarDate(prev.date),
            showEndDate: false,
          };
        }

        return {
          ...prev,
          flightType: nextFlightType,
          departureAirfield: baseAirfield,
          destinationAirfield: baseAirfield,
          endDate: prev.date,
          showEndDate: false,
        };
      });

      return;
    }
    if (name === 'departureAirfield' || name === 'destinationAirfield') {
      setForm((prev) => ({
        ...prev,
        [name]: normalizeAirfieldCode(value),
      }));
      return;
    }

    if (name === 'aircraft') {
      setForm((prev) => ({
        ...prev,
        aircraft: value !== '' ? Number(value) : value,
      }));
      return;
    }

    if (name === 'durationInput') {
      setForm((prev) => applyDurationInput(prev, value));
      return;
    }

    if (name === 'date' || name === 'time') {
      setForm((prev) => {
        const next = {
          ...prev,
          [name]: value,
        };

        if (name === 'date' && next.endDate && next.endDate < value) {
          next.endDate = value;
        }

        if (!next.showEndDate) {
          next.endDate = next.date;
        }

        if (next.durationInput) {
          return applyDurationInput(next, next.durationInput);
        }

        return next;
      });
      return;
    }

    if (name === 'endDate' || name === 'endTime') {
      setForm((prev) => {
        const next = {
          ...prev,
          [name]: value,
        };

        const start = parseDateTime(next.date, next.time);
        const end = parseDateTime(next.endDate, next.endTime);
        return {
          ...next,
          durationInput: deriveDurationInput(start, end),
        };
      });
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    setSuccessMessage('');
    setSuccessBookingId('');

    const result = await submitBooking({
      supabase,
      user,
      currentMemberId: session?.profile?.id,
      form,
      editingBookingId,
      baseAirfield,
      trainingOptions,
      canCreateTrainingBooking,
      confirmBooking: window.confirm,
    });

    if (!result.ok) {
      setError(result.errorMessage);
      return;
    }

    setSuccessBookingId(result.bookingId);
    setSuccessMessage(result.successMessage);
    setEditingBookingId('');
    setBookingModalOpen(false);
    await fetchBookings();
  }

  async function handleDeleteActiveBooking() {
    if (!activeBooking?.id) return;

    const bookingToDelete = activeBooking;
    const bookingLabel = `${bookingToDelete.aircraftRegistration || 'Aircraft'} | ${formatBookingRange(bookingToDelete)}`;

    setError('');
    setDetailsError('');
    setSuccessMessage('');

    const result = await performBookingDeletion({
      booking: bookingToDelete,
      userId: user?.id,
      supabase,
      confirmDeletion: () => {
        const confirmed = window.confirm(`Delete this booking?\n\n${bookingLabel}\n\nThis action cannot be undone.`);
        if (confirmed) setDeletingBooking(true);
        return confirmed;
      },
    });

    setDeletingBooking(false);

    if (!result.ok) {
      if (result.reason === 'cancelled') return;

      if (result.reason === 'not-owner') {
        setDetailsError('You can only delete your own bookings.');
        return;
      }

      if (result.reason === 'booking_started') {
        setDetailsError(getBookingDeletionErrorMessage(result.reason));
        return;
      }

      if (result.reason === 'locked') {
        setDetailsError(
          'This booking can no longer be deleted because flight or technical-log records exist for it. Completed flight records must be retained.'
        );
        return;
      }

      setDetailsError(`Could not delete booking: ${result.error?.message || 'Unknown error'}`);
      return;
    }

    setDetailsModalOpen(false);
    setActiveBooking(null);
    setDetailsError('');
    setEditingBookingId('');
    setSuccessBookingId('');
    setSuccessMessage('Booking deleted successfully.');
    await fetchBookings();
  }

  return (
    <div className="wingit-book-aircraft-page" style={pageStyle}>
      <BookingPageHeader navigate={navigate} />

      {error && (
        <div
          style={{
            background: '#3a1111',
            color: '#ffb4b4',
            border: '1px solid #7d2c2c',
            borderRadius: 10,
            padding: '0.8rem 1rem',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}

      {successMessage && (
        <div
          style={{
            background: '#12351f',
            color: '#8cff8c',
            border: '1px solid #2f7d4d',
            borderRadius: 10,
            padding: '0.9rem 1rem',
            marginBottom: '1rem',
          }}
        >
          <div style={{ fontWeight: 800 }}>{successMessage}</div>

          {successBookingId && (
            <div style={{ marginTop: '0.8rem', display: 'flex', gap: '0.7rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => navigate(`/pre-flight?bookingId=${encodeURIComponent(successBookingId)}`)}
                style={buttonStyle}
              >
                Start Flight Planning
              </button>

              <button
                type="button"
                onClick={() => navigate('/my-bookings')}
                style={secondaryButtonStyle}
              >
                View My Flights
              </button>
            </div>
          )}
        </div>
      )}

      <BookingCalendarPanel
        calendarRef={calendarRef}
        calendarRenderKey={calendarRenderKey}
        calendarDate={calendarDate}
        selectedDate={selectedDate}
        setCalendarDate={setCalendarDate}
        events={events}
        bookings={bookings}
        aircraftList={aircraftList}
        trainingOptions={trainingOptions}
        usersMap={usersMap}
        setSelectedDate={setSelectedDate}
        openBookingModal={openBookingModal}
        setActiveBooking={setActiveBooking}
        setDetailsModalOpen={setDetailsModalOpen}
        formatLocalDate={formatLocalDate}
        showOperationsBoard={Boolean(selectedDateFromUrl)}
        openDayView={openDayView}
        returnToCalendar={returnToCalendar}
        changeDayView={changeDayView}
      />

      <BookingModal
        open={bookingModalOpen}
        onClose={() => {
          const wasEditing = Boolean(editingBookingId);
          setBookingModalOpen(false);
          setEditingBookingId('');
          setEndDatePickerOpen(false);
          setError('');
          if (wasEditing && activeBooking) {
            setDetailsModalOpen(true);
          }
        }}
        editingBookingId={editingBookingId}
        error={error || trainingOptionsError}
        handleSubmit={handleSubmit}
        form={form}
        handleChange={handleChange}
        aircraftList={aircraftList}
        trainingOptions={trainingOptions}
        trainingOptionsLoading={trainingOptionsLoading}
        usersMap={usersMap}
        canCreateTrainingBooking={canCreateTrainingBooking}
        buildAircraftDisplayLabel={buildAircraftDisplayLabel}
        selectedAircraft={selectedAircraft}
        getAircraftServiceabilityMeta={getAircraftServiceabilityMeta}
        startDatePickerRef={startDatePickerRef}
        startDatePickerOpen={startDatePickerOpen}
        setStartDatePickerOpen={setStartDatePickerOpen}
        endDatePickerRef={endDatePickerRef}
        endDatePickerOpen={endDatePickerOpen}
        setEndDatePickerOpen={setEndDatePickerOpen}
        setForm={setForm}
        parseDateOnly={parseDateOnly}
        formatLocalDate={formatLocalDate}
        parseDateTime={parseDateTime}
        applyDurationInput={applyDurationInput}
        applyDurationPreset={applyDurationPreset}
        deriveDurationInput={deriveDurationInput}
        baseAirfield={baseAirfield}
        inputStyle={inputStyle}
        buttonStyle={buttonStyle}
      />

      <BookingDetailsModal
        open={detailsModalOpen}
        activeBooking={activeBooking}
        detailsError={detailsError}
        isActiveBookingOwner={isActiveBookingOwner}
        canEditTrainingAssignment={
          activeBooking?.booking_type === 'training' && canCreateTrainingBooking
        }
        aircraftList={aircraftList}
        deletingBooking={deletingBooking}
        getAircraftChipStyle={getAircraftChipStyle}
        getAircraftColour={getAircraftColour}
        openBookingModal={openBookingModal}
        handleDeleteActiveBooking={handleDeleteActiveBooking}
        closeDetailsModal={dismissDetailsModal}
        closeDetailsModalWithReturn={
          closeDetailsModalWithReturn
        }
        buttonStyle={buttonStyle}
        secondaryButtonStyle={secondaryButtonStyle}
      />
    </div>
  );
}